import { useSyncExternalStore } from 'react';

import { definirAutorDoRetorno } from '@/lib/retorno';

import type { UsuarioDaSessao } from './consultas';

/**
 * A sessão lida FORA de componente.
 *
 * Este arquivo substitui `persona.ts`, e o mecanismo é o mesmo pelo mesmo
 * motivo: `FormularioAbastecimento` precisa saber quem está preenchendo o
 * formulário, e ele pergunta de dentro de `rascunhoInicial()`, que é uma
 * função comum e não um componente. Um `useContext` não a alcança; um
 * `useQuery` também não.
 *
 * O que mudou foi o conteúdo. Antes guardava a persona escolhida num seletor —
 * uma escolha que não chegava a servidor nenhum e que a tela de login
 * desmentia três vezes. Agora guarda o espelho da sessão de verdade, e quem o
 * atualiza é `useSessao()`, na resposta de `GET /api/sessao/atual`.
 *
 * **Não é a fonte da verdade, e não decide nada.** A fonte é o cookie
 * `HttpOnly` que o navegador guarda e que este código não alcança; quem
 * resolve o cookie é o banco, a cada requisição. Isto aqui é só o último valor
 * visto, para a tela não ter de esperar por uma consulta para escrever um
 * nome. Apagar este arquivo não abriria porta nenhuma.
 *
 * Nada vai para o `localStorage`: identidade que volta sozinha depois de
 * fechar o navegador seria uma segunda sessão, com prazo próprio, ao lado da
 * que o banco controla.
 */

let atual: UsuarioDaSessao | null = null;

const ouvintes = new Set<() => void>();

function inscrever(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

function ler(): UsuarioDaSessao | null {
  return atual;
}

/** Chamado por `useSessao()` — e por mais ninguém. */
export function guardarSessao(sessao: UsuarioDaSessao | null): void {
  if (atual === sessao) return;
  atual = sessao;
  /* Quem assina o toast (R04, p.32: "registro, hora e autor"). */
  definirAutorDoRetorno(sessao?.nome);
  for (const ouvinte of ouvintes) ouvinte();
}

/** Leitura fora de componente. Dentro de componente, use `useSessaoEmMemoria`. */
export function sessaoEmMemoria(): UsuarioDaSessao | null {
  return atual;
}

export function useSessaoEmMemoria(): UsuarioDaSessao | null {
  return useSyncExternalStore(inscrever, ler, ler);
}
